import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";

const STATUS_COLS = [
  { key: "pendiente", label: "Pendiente", cls: "bg-amber-50/60 border-amber-200 text-amber-900" },
  { key: "en_curso",  label: "En curso",  cls: "bg-blue-50/60 border-blue-200 text-blue-900" },
  { key: "esperando", label: "Esperando", cls: "bg-purple-50/60 border-purple-200 text-purple-900" },
  { key: "resuelta",  label: "Resuelta",  cls: "bg-emerald-50/60 border-emerald-200 text-emerald-900" },
];

const CAT_META = {
  tarea:   { label: "Tarea",   cls: "bg-indigo-100 text-indigo-800" },
  mail:    { label: "Mail",    cls: "bg-yellow-100 text-yellow-900" },
  reunion: { label: "Reunión", cls: "bg-pink-100 text-pink-900" },
};

const WHO_META = {
  edu:    { initials: "E",  cls: "bg-[#941620] text-white" },
  manuel: { initials: "M",  cls: "bg-[#26775E] text-white" },
  both:   { initials: "EM", cls: "text-white", style: { background: "linear-gradient(90deg,#941620 50%,#26775E 50%)" } },
};

function Assignee({ list }) {
  if (!list || list.length === 0) return <span className="w-5 h-5 rounded-full bg-stone-200"></span>;
  const key = list.length > 1 ? "both" : list[0];
  const m = WHO_META[key] || { initials: list[0]?.[0]?.toUpperCase() || "?", cls: "bg-stone-500 text-white" };
  return (
    <span className={`inline-flex items-center justify-center rounded-full w-5 h-5 text-[9px] font-bold ${m.cls}`} style={m.style || {}}>
      {m.initials}
    </span>
  );
}

const TAB_CATEGORIES = [
  { key: "tarea",   label: "Tareas" },
  { key: "mail",    label: "Mails y contactos" },
  { key: "reunion", label: "Reuniones" },
];

export default function TareasPage() {
  const [tab, setTab] = useState("tarea");
  const [tasks, setTasks] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [projectF, setProjectF] = useState("");
  const [assigneeF, setAssigneeF] = useState("");
  const [activeOnly, setActiveOnly] = useState(true);
  const [view, setView] = useState(() => { try { return localStorage.getItem("tareas:view") || "kanban"; } catch { return "kanban"; } });
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [tRes, pRes] = await Promise.all([
        api.get("/tasks"),
        api.get("/icaa/projects").catch(() => ({ data: [] })),
      ]);
      setTasks(tRes.data.tasks || []);
      setProjects(Array.isArray(pRes.data) ? pRes.data : (pRes.data?.projects || []));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const projectTitle = (pid) => projects.find(p => p.id === pid)?.title || "";

  const filtered = useMemo(() => tasks.filter(t => {
    if (t.category !== tab) return false;
    if (activeOnly && (t.status === "resuelta" || t.status === "archivada")) return false;
    if (projectF && t.project_id !== projectF) return false;
    if (assigneeF && !t.assignees?.includes(assigneeF)) return false;
    if (q.trim()) {
      const s = q.toLowerCase();
      if (![t.title, t.next_task, t.description, t.contact_person].filter(Boolean).join(" ").toLowerCase().includes(s)) return false;
    }
    return true;
  }), [tasks, tab, activeOnly, projectF, assigneeF, q]);

  const byStatus = (status) => filtered.filter(t => t.status === status);

  const changeView = (v) => { setView(v); try { localStorage.setItem("tareas:view", v); } catch (_e) {} };

  const moveStatus = async (task, newStatus) => {
    setTasks(list => list.map(t => t.id === task.id ? { ...t, status: newStatus } : t));
    try { await api.patch(`/tasks/${task.id}`, { status: newStatus }); } catch (_e) { load(); }
  };

  return (
    <div className="min-h-screen bg-gb-bg p-6" data-testid="tareas-page">
      <header className="mb-5">
        <p className="text-[10px] uppercase tracking-[0.4em] text-[#26775E] font-semibold">Diario</p>
        <div className="flex items-center justify-between mt-1">
          <h1 className="font-serif text-3xl">Tareas y seguimiento</h1>
          <button data-testid="tareas-new"
                  onClick={() => setEditing({ category: tab, status: "pendiente", priority: "media", assignees: [] })}
                  className="rounded-full bg-[#941620] hover:bg-[#7a1119] text-white px-4 py-1.5 text-[11px] uppercase tracking-widest font-semibold">
            + Nueva tarea
          </button>
        </div>
      </header>

      {/* Tabs */}
      <div className="inline-flex rounded-full border border-stone-300 bg-white p-1 text-[11px] uppercase tracking-[0.25em] mb-4">
        {TAB_CATEGORIES.map(c => {
          const n = tasks.filter(t => t.category === c.key && (!activeOnly || (t.status !== "resuelta" && t.status !== "archivada"))).length;
          return (
            <button key={c.key} onClick={() => setTab(c.key)}
                    data-testid={`tareas-tab-${c.key}`}
                    className={`rounded-full px-4 py-1.5 ${tab === c.key ? "bg-stone-900 text-[#FAFAF6]" : "text-stone-500"}`}>
              {c.label} <span className="ml-1 opacity-70">({n})</span>
            </button>
          );
        })}
      </div>

      {/* Filtros */}
      <div className="rounded-xl border border-stone-200 bg-white p-3 mb-4">
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <input placeholder="Buscar…" value={q} onChange={(e) => setQ(e.target.value)}
                 data-testid="tareas-search"
                 className="rounded-md border border-stone-300 px-3 py-1.5 text-sm w-56" />
          <select value={projectF} onChange={(e) => setProjectF(e.target.value)}
                  data-testid="tareas-filter-project"
                  className="rounded-md border border-stone-300 px-2 py-1.5 text-sm">
            <option value="">Todos los proyectos</option>
            {projects.map(p => <option key={p.id} value={p.id}>{p.title}</option>)}
          </select>
          <select value={assigneeF} onChange={(e) => setAssigneeF(e.target.value)}
                  data-testid="tareas-filter-assignee"
                  className="rounded-md border border-stone-300 px-2 py-1.5 text-sm">
            <option value="">Todos los responsables</option>
            <option value="edu">Edu</option>
            <option value="manuel">Manuel</option>
          </select>
          <label className="flex items-center gap-1 text-[12px] cursor-pointer">
            <input type="checkbox" checked={activeOnly} onChange={(e) => setActiveOnly(e.target.checked)}
                   data-testid="tareas-active-only" />
            Solo activas
          </label>
          <div className="ml-auto inline-flex rounded-full border border-stone-300 bg-white p-1 text-[11px] uppercase tracking-[0.2em]">
            <button onClick={() => changeView("kanban")} data-testid="tareas-view-kanban"
                    className={`rounded-full px-3 py-1 ${view === "kanban" ? "bg-stone-900 text-[#FAFAF6]" : "text-stone-600"}`}>Kanban</button>
            <button onClick={() => changeView("list")} data-testid="tareas-view-list"
                    className={`rounded-full px-3 py-1 ${view === "list" ? "bg-stone-900 text-[#FAFAF6]" : "text-stone-600"}`}>Lista</button>
          </div>
        </div>
      </div>

      {loading && <div className="text-center text-stone-400 py-10">Cargando…</div>}

      {!loading && view === "kanban" && (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
          {STATUS_COLS.filter(c => activeOnly ? c.key !== "resuelta" : true).map(col => {
            const items = byStatus(col.key);
            return (
              <div key={col.key} data-testid={`tareas-col-${col.key}`}
                   className={`rounded-xl border p-3 min-h-[400px] ${col.cls}`}>
                <div className="flex items-center justify-between mb-3">
                  <p className="text-[11px] uppercase tracking-widest font-semibold">{col.label}</p>
                  <span className="bg-white/60 rounded-full px-2 py-0.5 text-[10px] font-bold">{items.length}</span>
                </div>
                <div className="space-y-2">
                  {items.map(t => (
                    <button key={t.id} onClick={() => setEditing(t)}
                            data-testid={`tareas-card-${t.id}`}
                            className="w-full text-left rounded-lg bg-white shadow-sm border border-stone-200 p-3 hover:shadow-md transition">
                      {t.project_id && <p className="text-[10px] uppercase tracking-widest text-stone-500 mb-1">{projectTitle(t.project_id)}</p>}
                      <p className="text-[13px] font-medium leading-tight">{t.title}</p>
                      {t.next_task && <p className="text-[11px] text-stone-500 mt-1 italic truncate">Sig: {t.next_task}</p>}
                      <div className="mt-2 flex items-center gap-2">
                        <Assignee list={t.assignees} />
                        <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[9px] font-semibold uppercase tracking-wider ${(CAT_META[t.category] || CAT_META.tarea).cls}`}>{(CAT_META[t.category] || CAT_META.tarea).label}</span>
                        <span className="ml-auto text-[10px] text-stone-500">{t.due_date || t.due_week || t.due_relative || ""}</span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {!loading && view === "list" && (
        <div className="rounded-xl border border-stone-200 bg-white overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-stone-200 bg-stone-50 text-left text-[10px] uppercase tracking-widest text-stone-500">
                <th className="px-3 py-2">Título</th>
                <th className="px-3 py-2">Proyecto</th>
                <th className="px-3 py-2">Estado</th>
                <th className="px-3 py-2">Resp.</th>
                <th className="px-3 py-2">Fecha</th>
                <th className="px-3 py-2">Siguiente</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(t => (
                <tr key={t.id} onClick={() => setEditing(t)}
                    data-testid={`tareas-row-${t.id}`}
                    className="border-b border-stone-100 hover:bg-stone-50 cursor-pointer">
                  <td className="px-3 py-2.5">
                    <p className="font-medium">{t.title}</p>
                  </td>
                  <td className="px-3 py-2.5 text-[12px] text-stone-700">{projectTitle(t.project_id)}</td>
                  <td className="px-3 py-2.5">
                    <select value={t.status} onClick={(e) => e.stopPropagation()}
                            onChange={(e) => moveStatus(t, e.target.value)}
                            className="rounded-full border border-stone-300 px-2 py-0.5 text-[11px]">
                      {STATUS_COLS.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
                      <option value="archivada">Archivada</option>
                    </select>
                  </td>
                  <td className="px-3 py-2.5"><Assignee list={t.assignees} /></td>
                  <td className="px-3 py-2.5 text-[11px] text-stone-700">{t.due_date || t.due_week || t.due_relative || "—"}</td>
                  <td className="px-3 py-2.5 text-[11px] text-stone-500 italic max-w-[200px] truncate">{t.next_task || ""}</td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={6} className="p-8 text-center text-stone-500">Sin tareas.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {editing && <TaskEditModal task={editing} projects={projects} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function TaskEditModal({ task, projects, onClose, onSaved }) {
  const [f, setF] = useState({
    title: task.title || "",
    description: task.description || "",
    project_id: task.project_id || "",
    category: task.category || "tarea",
    status: task.status || "pendiente",
    priority: task.priority || "media",
    assignees: task.assignees || [],
    due_date: task.due_date || "",
    due_week: task.due_week || "",
    due_relative: task.due_relative || "",
    next_task: task.next_task || "",
    last_movement: task.last_movement || "",
    resolution: task.resolution || "",
    contact_person: task.contact_person || "",
  });
  const [saving, setSaving] = useState(false);

  const toggleAssignee = (a) => {
    setF(prev => ({
      ...prev,
      assignees: prev.assignees.includes(a)
        ? prev.assignees.filter(x => x !== a)
        : [...prev.assignees, a],
    }));
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...f };
      payload.due_date_type = payload.due_date ? "date" : payload.due_week ? "week" : payload.due_relative ? "relative" : "none";
      if (!payload.project_id) payload.project_id = null;
      if (task.id) await api.patch(`/tasks/${task.id}`, payload);
      else await api.post("/tasks", payload);
      onSaved();
    } finally { setSaving(false); }
  };

  const remove = async () => {
    if (!task.id) return onClose();
    if (!window.confirm("¿Borrar esta tarea?")) return;
    await api.delete(`/tasks/${task.id}`);
    onSaved();
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/40 p-4" data-testid="task-edit-modal">
      <div className="w-full max-w-2xl overflow-hidden rounded-2xl bg-white shadow-2xl">
        <div className="flex items-start justify-between border-b border-stone-200 bg-gradient-to-br from-[#941620] to-[#7a1119] px-6 py-4 text-white">
          <div>
            <p className="text-[10px] uppercase tracking-[0.3em] text-white/70">{task.id ? "Editar" : "Nueva"} tarea</p>
            <h2 className="mt-0.5 font-serif text-xl">{f.title || "Sin título"}</h2>
          </div>
          <button onClick={onClose} className="rounded-full bg-white/20 px-2.5 py-0.5 text-lg text-white hover:bg-white/30">×</button>
        </div>
        <div className="max-h-[70vh] overflow-y-auto px-6 py-4 space-y-3">
          <div>
            <label className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold">Título</label>
            <input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })}
                   data-testid="task-form-title"
                   className="w-full rounded-md border border-stone-300 px-3 py-1.5 text-sm" />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold">Proyecto</label>
              <select value={f.project_id} onChange={(e) => setF({ ...f, project_id: e.target.value })}
                      data-testid="task-form-project"
                      className="w-full rounded-md border border-stone-300 px-2 py-1.5 text-sm">
                <option value="">— sin proyecto —</option>
                {projects.map(p => <option key={p.id} value={p.id}>{p.title}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold">Categoría</label>
              <select value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })}
                      className="w-full rounded-md border border-stone-300 px-2 py-1.5 text-sm">
                <option value="tarea">Tarea</option>
                <option value="mail">Mail / contacto</option>
                <option value="reunion">Reunión</option>
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold">Prioridad</label>
              <select value={f.priority} onChange={(e) => setF({ ...f, priority: e.target.value })}
                      className="w-full rounded-md border border-stone-300 px-2 py-1.5 text-sm">
                <option value="alta">Alta</option>
                <option value="media">Media</option>
                <option value="baja">Baja</option>
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold">Estado</label>
              <select value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })}
                      data-testid="task-form-status"
                      className="w-full rounded-md border border-stone-300 px-2 py-1.5 text-sm">
                <option value="pendiente">Pendiente</option>
                <option value="en_curso">En curso</option>
                <option value="esperando">Esperando</option>
                <option value="resuelta">Resuelta</option>
                <option value="archivada">Archivada</option>
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold">Responsables</label>
              <div className="flex gap-1 mt-1">
                {["edu", "manuel"].map(a => (
                  <button key={a} type="button" onClick={() => toggleAssignee(a)}
                          data-testid={`task-form-assignee-${a}`}
                          className={`rounded-full px-3 py-1 text-[11px] uppercase tracking-widest font-semibold ${f.assignees.includes(a) ? (a === "edu" ? "bg-[#941620] text-white" : "bg-[#26775E] text-white") : "border border-stone-300 text-stone-600"}`}>
                    {a === "edu" ? "Edu" : "Manuel"}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold">Fecha</label>
              <input type="date" value={f.due_date} onChange={(e) => setF({ ...f, due_date: e.target.value, due_week: "", due_relative: "" })}
                     data-testid="task-form-date"
                     className="w-full rounded-md border border-stone-300 px-2 py-1.5 text-sm" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold">o Semana</label>
              <input value={f.due_week} onChange={(e) => setF({ ...f, due_week: e.target.value, due_date: "", due_relative: "" })}
                     placeholder="Semana 28/09" className="w-full rounded-md border border-stone-300 px-2 py-1.5 text-sm" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold">o Relativo</label>
              <input value={f.due_relative} onChange={(e) => setF({ ...f, due_relative: e.target.value, due_date: "", due_week: "" })}
                     placeholder="Tras reunión..." className="w-full rounded-md border border-stone-300 px-2 py-1.5 text-sm" />
            </div>
          </div>
          {f.category === "mail" && (
            <div>
              <label className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold">Persona / empresa</label>
              <input value={f.contact_person} onChange={(e) => setF({ ...f, contact_person: e.target.value })}
                     className="w-full rounded-md border border-stone-300 px-3 py-1.5 text-sm" />
            </div>
          )}
          <div>
            <label className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold">Siguiente tarea</label>
            <input value={f.next_task} onChange={(e) => setF({ ...f, next_task: e.target.value })}
                   data-testid="task-form-next"
                   className="w-full rounded-md border border-stone-300 px-3 py-1.5 text-sm" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold">Último movimiento</label>
              <textarea rows={2} value={f.last_movement} onChange={(e) => setF({ ...f, last_movement: e.target.value })}
                        className="w-full rounded-md border border-stone-300 px-3 py-1.5 text-sm" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold">Resolución</label>
              <textarea rows={2} value={f.resolution} onChange={(e) => setF({ ...f, resolution: e.target.value })}
                        className="w-full rounded-md border border-stone-300 px-3 py-1.5 text-sm" />
            </div>
          </div>
        </div>
        <div className="flex items-center justify-between border-t border-stone-200 bg-stone-50 px-6 py-3">
          {task.id ? (
            <button onClick={remove} className="text-[11px] uppercase tracking-widest text-rose-700 hover:underline font-semibold">🗑 Borrar</button>
          ) : <span />}
          <div className="flex gap-2">
            <button onClick={onClose} className="rounded-full border border-stone-300 px-4 py-1.5 text-[11px] uppercase tracking-widest font-semibold">Cancelar</button>
            <button onClick={save} disabled={saving || !f.title}
                    data-testid="task-form-save"
                    className="rounded-full bg-[#941620] hover:bg-[#7a1119] text-white px-5 py-1.5 text-[11px] uppercase tracking-widest font-semibold disabled:opacity-50">
              {saving ? "Guardando…" : "Guardar"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
